/**
 * Consultation Report Preview Page
 * 
 * Displays the live HTML preview of the market appraisal report
 * generated from structured inputs.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Alert, 
  Typography, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { ArrowBack, CheckCircle, Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { StorageManager } from '../utils/storageManager';

const ConsultationReportPreview: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [propertyOwnerEmail, setPropertyOwnerEmail] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [structuredInputs, setStructuredInputs] = useState<any>(null);

  useEffect(() => {
    // Small delay to ensure token is available in new tab
    const timer = setTimeout(() => {
      // Check if we have current UI state in sessionStorage (from dialog)
      const storedInputs = propertyId 
        ? sessionStorage.getItem(`appraisal_preview_${propertyId}`)
        : null;
      
      let inputsFromUI: any = null;
      if (storedInputs) {
        try {
          inputsFromUI = JSON.parse(storedInputs);
          // Clear it after reading so it's only used once
          sessionStorage.removeItem(`appraisal_preview_${propertyId}`);
        } catch (err) {
          console.error('Error parsing stored inputs:', err);
        }
      }
      
      // Use current UI state if available, otherwise fetch from DB
      if (inputsFromUI) {
        fetchPreview(inputsFromUI);
        setStructuredInputs(inputsFromUI);
      } else {
        fetchPreview();
        fetchStructuredInputs();
      }
      
      fetchPropertyOwnerEmail();
    }, 100); // Small delay to ensure localStorage is accessible in new tab
    
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencySlug, propertyId]);

  const fetchStructuredInputs = async () => {
    if (!agencySlug || !propertyId) return;
    
    try {
      const response = await api.get(
        `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`
      );
      // The response includes savedStructuredInputs if available
      if (response.data.savedStructuredInputs) {
        setStructuredInputs(response.data.savedStructuredInputs);
      } else {
        // If no saved inputs, construct from the response data
        // Import dynamically to avoid issues
        const prepareAppraisalInputsModule = await import('../utils/prepareAppraisalInputs');
        const inputs = prepareAppraisalInputsModule.prepareAppraisalInputs(
          response.data.property,
          response.data.agency,
          response.data.agent,
          response.data.extractedData
        );
        setStructuredInputs(inputs);
      }
    } catch (err) {
      console.error('Error fetching structured inputs:', err);
      // Don't set error here - preview can still work without structuredInputs
    }
  };

  const fetchPreview = async (inputsToUse?: any) => {
    if (!agencySlug || !propertyId) {
      setError('Missing agency or property ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Use StorageManager to get token (same as api service)
      const token = StorageManager.getToken();
      
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }
      
      // If structuredInputs are provided (from dialog/UI), use POST to send them
      // Otherwise use GET to fetch from DB
      const isPost = !!inputsToUse;
      
      // Use api service for both GET and POST (handles auth automatically via interceptors)
      // Note: axios merges headers, so Authorization from interceptor will be preserved
      if (isPost) {
        // POST with structuredInputs (current UI state)
        const response = await api.post(
          `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/preview`,
          { structuredInputs: inputsToUse },
          {
            responseType: 'text', // Get HTML as text
            headers: {
              'Accept': 'text/html',
              'Content-Type': 'application/json' // Ensure Content-Type is set for POST
            }
          }
        );
        setHtmlContent(response.data);
      } else {
        // GET to fetch from saved DB state
        const response = await api.get(
          `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/preview`,
          {
            responseType: 'text', // Get HTML as text
            headers: {
              'Accept': 'text/html'
            }
          }
        );
        setHtmlContent(response.data);
      }
    } catch (err: any) {
      console.error('Error fetching preview:', err);
      setError(
        err.response?.data?.error || 
        err.message || 
        'Failed to load preview. Please ensure the report has been generated first.'
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyOwnerEmail = async () => {
    if (!agencySlug || !propertyId) return;
    
    try {
      const response = await api.get(`/agencies/${agencySlug}/properties/${propertyId}`);
      const owner = response.data.owner;
      if (owner?.email) {
        setPropertyOwnerEmail(owner.email);
        if (!sendEmail) {
          setSendEmail(owner.email);
        }
      }
    } catch (err) {
      console.error('Error fetching property owner email:', err);
    }
  };

  const handleFinalize = () => {
    setShowFinalizeDialog(true);
  };

  const confirmFinalize = async (shouldSend: boolean) => {
    if (!agencySlug || !propertyId) return;
    
    setShowFinalizeDialog(false);
    setFinalizing(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Step 1: Fetch the latest structured inputs if not already loaded
      let latestInputs = structuredInputs;
      if (!latestInputs) {
        try {
          const inputsResponse = await api.get(
            `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/inputs`
          );
          if (inputsResponse.data.savedStructuredInputs) {
            latestInputs = inputsResponse.data.savedStructuredInputs;
          }
        } catch (err) {
          console.warn('Could not fetch structured inputs, proceeding with saved data:', err);
        }
      }
      
      // Step 2: Save the latest structured inputs to ensure backend has current data
      if (latestInputs) {
        try {
          await api.put(
            `/agencies/${agencySlug}/properties/${propertyId}/consultation-report`,
            { structuredInputs: latestInputs }
          );
          console.log('Saved structured inputs before finalizing');
        } catch (err) {
          console.warn('Could not save structured inputs, proceeding with finalize:', err);
          // Continue anyway - backend will use saved structuredInputs if available
        }
      }
      
      // Step 3: Finalize the report asynchronously (backend will use saved structuredInputs)
      const response = await api.post(
        `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/finalize-async`,
        latestInputs ? { structuredInputs: latestInputs } : {}
      );
      
      setSuccess('Report finalization started. You will be notified when it completes.');
      
      // Since finalization is async, we can't send immediately
      // User will be notified when finalization completes, then they can send
      if (shouldSend) {
        setSuccess('Report finalization started. Once finalized, you will be notified and can send it from the property page.');
      }
      
      // Redirect back to the property page after a short delay to show success message
      setTimeout(() => {
        navigate(`/agencies/${agencySlug}/properties/${propertyId}`);
      }, 2000);
    } catch (err: any) {
      console.error('Error finalizing report:', err);
      let errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to finalize report';
      
      // Provide more helpful error message if content is missing
      if (errorMessage.includes('content is missing') || errorMessage.includes('generate the report first')) {
        errorMessage = 'Report content is missing. Please generate the PDF first by clicking "Confirm & Generate PDF" in the edit page.';
      }
      
      setError(errorMessage);
      console.error('Full error response:', err.response?.data);
    } finally {
      setFinalizing(false);
    }
  };

  const handleSend = async () => {
    if (!agencySlug || !propertyId) return;
    
    if (!sendEmail || !sendEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    setSending(true);
    setError(null);
    setSuccess(null);
    
    try {
      await api.post(
        `/agencies/${agencySlug}/properties/${propertyId}/consultation-report/send-email`,
        { email: sendEmail }
      );
      
      setSuccess(`Report sent successfully to ${sendEmail}`);
      setShowSendDialog(false);
      
      // Redirect to edit page after a short delay
      setTimeout(() => {
        navigate(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/edit`);
      }, 1500);
    } catch (err: any) {
      console.error('Error sending report:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/edit`)}
          sx={{ mb: 2 }}
        >
          Back to Edit
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!htmlContent) {
    return (
      <Box sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/edit`)}
          sx={{ mb: 2 }}
        >
          Back to Edit
        </Button>
        <Alert severity="warning">No preview content available.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with back button and finalize button */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/agencies/${agencySlug}/properties/${propertyId}/consultation-report/edit`)}
          disabled={finalizing || sending}
        >
          Back to Edit
        </Button>
        <Typography variant="h6" sx={{ flex: 1 }}>Market Appraisal Preview</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={finalizing ? <CircularProgress size={20} /> : <CheckCircle />}
          onClick={handleFinalize}
          disabled={finalizing || sending}
        >
          {finalizing ? 'Finalizing...' : 'Finalize Report'}
        </Button>
      </Box>

      {/* Success/Error messages */}
      {success && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Box>
      )}
      {error && (
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Box>
      )}

      {/* HTML content in iframe for better isolation */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <iframe
          srcDoc={htmlContent}
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
            overflow: 'auto'
          }}
          title="Market Appraisal Preview"
        />
      </Box>

      {/* Finalize Confirmation Dialog */}
      <Dialog open={showFinalizeDialog} onClose={() => setShowFinalizeDialog(false)}>
        <DialogTitle>Finalize Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you ready to finalize this market appraisal report? Once finalized, the report will be locked and cannot be edited.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Would you like to send the report to the vendor after finalizing?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFinalizeDialog(false)} disabled={finalizing}>
            Cancel
          </Button>
          <Button onClick={() => confirmFinalize(false)} disabled={finalizing} variant="outlined">
            Finalize Only
          </Button>
          <Button 
            onClick={() => confirmFinalize(true)} 
            disabled={finalizing} 
            variant="contained"
            startIcon={<Send />}
          >
            Finalize & Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Report Dialog */}
      <Dialog open={showSendDialog} onClose={() => setShowSendDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Report to Vendor</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the vendor's email address to send the finalized report.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              type="email"
              value={sendEmail}
              onChange={(e) => setSendEmail(e.target.value)}
              placeholder="vendor@example.com"
              style={{
                padding: '12px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                width: '100%'
              }}
              disabled={sending}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSendDialog(false)} disabled={sending}>
            Cancel
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={sending || !sendEmail || !sendEmail.includes('@')} 
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <Send />}
          >
            {sending ? 'Sending...' : 'Send Report'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationReportPreview;
